import { Vector3 } from 'three/webgpu';
import { type HollowDef, makeHollow } from '../universe/HollowDef';
import type { SpiralOptions } from '../universe/Tunnel';
import { buildEntrances, type Entrance, entranceOptions, legacyPhaseB } from './entrance';
import type { InteriorSpec } from './layout';
import type { V3 } from './sdf';

/**
 * Test caves (not used by the game): two worlds, their entrances, and the spec for the
 * interior between the gates, in cave-local coordinates (origin halfway between the gates).
 */
export interface CaveFixture {
  ra: number;
  rb: number;
  ha: number;
  hb: number;
  oldGap: number;
  gap: number;
  spiral: SpiralOptions;
  /** Where round the axis mouth B is (as custom worlds choose it), instead of the old spiral's end. */
  phaseB?: number;
}

export const BIG_CAVE: CaveFixture = {
  ra: 12000,
  rb: 5000,
  ha: 60,
  hb: 8.5,
  oldGap: 260,
  gap: 450,
  spiral: { tubeRadius: 4, helixRadius: 24, pitchDeg: 24, depth: 10, phase: 0.6 },
};

export const SMALL_CAVE: CaveFixture = {
  ra: 420,
  rb: 300,
  ha: 4,
  hb: 2,
  oldGap: 60,
  gap: 100,
  spiral: { tubeRadius: 3.5, helixRadius: 14, pitchDeg: 24, depth: 6, phase: 0.4 },
};

const FAR = new Vector3(1.0e7, 2.0e5, -3.0e6);
const AXIS = new Vector3(0.098, 0.7809, -0.617).normalize();

export interface BuiltCave {
  a: HollowDef;
  b: HollowDef;
  ends: [Entrance, Entrance];
  /** Universe position of the cave-local origin. */
  origin: Vector3;
  spec: InteriorSpec;
}

export function buildFixture(f: CaveFixture, seed = 1): BuiltCave {
  const hollow = (id: string, c: Vector3, r: number) =>
    makeHollow({ id, name: id, center: [c.x, c.y, c.z], radius: r, seed: 1, sun: null });
  const a = hollow('a', FAR, f.ra);
  const b = hollow('b', FAR.clone().addScaledVector(AXIS, f.ra + f.gap + f.rb), f.rb);
  const pa = hollow('a', a.center, f.ra - f.ha);
  const pb = hollow('b', b.center, f.rb - f.hb);
  const plan = { ...f.spiral, phaseB: f.phaseB ?? legacyPhaseB(f.spiral, f.oldGap, f.ha, f.hb) };
  const ends = buildEntrances(pa, pb, [f.ra, f.rb], plan, [
    entranceOptions(f.ra, f.spiral.tubeRadius),
    entranceOptions(f.rb, f.spiral.tubeRadius),
  ]);
  const origin = ends[0].gate.clone().add(ends[1].gate).multiplyScalar(0.5);
  const local = (v: Vector3): V3 => [v.x - origin.x, v.y - origin.y, v.z - origin.z];
  const dir = (v: Vector3): V3 => [v.x, v.y, v.z];
  const spec: InteriorSpec = {
    seed,
    gates: [a, b].map((h, k) => ({
      p: local(ends[k].gate),
      dir: dir(ends[k].gateDir),
      down: dir(ends[k].down),
      radius: ends[k].radius,
      g: h.gravity,
    })) as InteriorSpec['gates'],
    avoid: [a, b].map((h) => ({ c: local(h.center), r: h.radius })),
    avoidTubes: ends.map((e) => ({ points: e.path.slice(e.helixStart).map(local), radius: e.radius })),
  };
  return { a, b, ends, origin, spec };
}
